import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Divider } from "@mui/material";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { AggregateScatterPoint } from "@/types";
import { useChartPalette } from "../utils/chartUtils";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";

interface AggMonthlyFactorCompositePremiumProps {
  data: AggregateScatterPoint[];
}

/**
 * Scatter plot of Agg Monthly Factor vs Composite Premium per policy.
 * Each dot is colored by carrier.
 */
export const AggMonthlyFactorCompositePremium = ({ data: rawData }: AggMonthlyFactorCompositePremiumProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const data = rawData.map((d) => ({
    x: d.monthly_factor,
    y: d.aggregate_premium,
    carrier: d.carrier_name,
    policyholder: d.client_name,
  }));

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  const carriers = [...new Set(data.map((d) => d.carrier))];

  const getColor = (carrier: string) => {
    const index = carriers.indexOf(carrier);
    return chartPalette[index % chartPalette.length];
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis
          type="number"
          dataKey="x"
          name="Agg Monthly Factor"
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis
          type="number"
          dataKey="y"
          name="Composite Premium"
          tickFormatter={(value) => formatUSD(value)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <Tooltip
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const { payload: item } = payload[0];
            return (
              <div
                style={{
                  ...getChartTooltipStyle(theme),
                  padding: "8px",
                }}
              >
                <strong>{item.policyholder}</strong>
                <div>{item.carrier}</div>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <div>Composite Premium: {formatUSD(item.y)}</div>
                <div>Agg Monthly Factor: {formatUSD(item.x, currencyCompactNotation)}</div>
              </div>
            );
          }}
        />
        <Scatter data={data}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.carrier)} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
